import { closePackageModal } from 'lib/openPackageModal'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import AcknowledgementResponseDrawer from './AcknowledgementResponseDrawer'
import { finalizeRound, getAcknowledgementResponse } from './api'
import { ACKNOWLEDGEMENT_RESPONSE_STATE } from './constants'
import AcknowledgementSuccessModal from './modals/AcknowledgementSuccessModal'

const DRAWER_DURATION = 600
const POLL_INTERVAL_MS = 2000
const MAX_POLL_ATTEMPTS = 15

export default function AcknowledgementPreviewModal({ packageId, acknowledgementContent }) {
  const [acknowledgementResponse, setAcknowledgementResponse] = useState(null)
  const [pollError, setPollError] = useState(null)
  const [openDrawer, setOpenDrawer] = useState(true)
  const [openSuccessModal, setOpenSuccessModal] = useState(false)

  const intervalRef = useRef(null)
  const attemptsRef = useRef(0)
  const submittingRef = useRef(false)

  const stopPolling = useCallback(() => {
    clearInterval(intervalRef.current)
    intervalRef.current = null
  }, [])

  const startPolling = useCallback(() => {
    attemptsRef.current = 0
    clearInterval(intervalRef.current)
    intervalRef.current = setInterval(async () => {
      if (++attemptsRef.current > MAX_POLL_ATTEMPTS) {
        stopPolling()
        setPollError('PDF generation timed out. Please try again.')
        return
      }
      try {
        const { data } = await getAcknowledgementResponse(packageId)
        if (data.status === 'ok') {
          setAcknowledgementResponse(data)
          stopPolling()
        }
      } catch (error) {
        console.error('[AcknowledgementPreviewModal] polling error', error)
      }
    }, POLL_INTERVAL_MS)
  }, [packageId, stopPolling])

  // Fresh root per openPackageModal call, so this only ever runs once per mount -- no
  // packageId/startPolling deps needed since neither can change out from under a live instance.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const { data } = await getAcknowledgementResponse(packageId)
        if (cancelled) return
        setAcknowledgementResponse(data)
        if (data.status === 'pending') startPolling()
      } catch (error) {
        console.error('[AcknowledgementPreviewModal] prefetch error', error)
        closePackageModal()
      }
    })()
    return () => {
      cancelled = true
      stopPolling()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleDownloadPdf = () => {
    const pdfUrl = acknowledgementResponse?.pdf_url
    if (!pdfUrl) return
    const link = document.createElement('a')
    link.href = pdfUrl
    link.download = ''
    link.target = '_blank'
    link.rel = 'noopener noreferrer'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handleSubmit = async () => {
    if (submittingRef.current) return // guards synchronously within the same tick against a double click
    submittingRef.current = true
    stopPolling()
    setOpenDrawer(false)
    try {
      await finalizeRound(packageId)
      document.dispatchEvent(new CustomEvent('package:acknowledgement-response-sent', { detail: { packageId } }))
      setTimeout(() => setOpenSuccessModal(true), DRAWER_DURATION)
    } catch (error) {
      console.error('[AcknowledgementPreviewModal] finalizeRound error', error)
      submittingRef.current = false
      setOpenDrawer(true)
    }
  }

  const handleCloseDrawer = () => {
    stopPolling()
    setOpenDrawer(false)
    setTimeout(() => {
      closePackageModal()
    }, DRAWER_DURATION)
  }

  // finalizeRound already advanced the round server-side, but the timer sidebar card is rendered
  // server-side from ny_timer_data -- only a fresh document load moves it off 'response due'. So
  // dismissing this modal has to land on a freshly loaded Requirements page (DOME-1433): reload
  // when the processor is already there, navigate when they were on some other tab. The modal is
  // deliberately left mounted until the browser unloads, so the stale page never flashes.
  const goToRequirements = () => {
    const requirementsPath = `/packages/${packageId}/documents`
    // Compared on pathname because assign() to a URL differing only by hash would not reload.
    if (window.location.pathname === requirementsPath) {
      window.location.reload()
    } else {
      window.location.assign(requirementsPath)
    }
  }

  // No response ever populated (permanently pending, then timeout) -> defaults to incomplete.
  const flow =
    acknowledgementResponse?.package_state === ACKNOWLEDGEMENT_RESPONSE_STATE.COMPLETE
      ? ACKNOWLEDGEMENT_RESPONSE_STATE.COMPLETE
      : ACKNOWLEDGEMENT_RESPONSE_STATE.INCOMPLETE

  return (
    <>
      <AcknowledgementResponseDrawer
        flow={flow}
        show={openDrawer}
        duration={DRAWER_DURATION}
        content={acknowledgementContent}
        acknowledgementResponse={acknowledgementResponse}
        pdfReady={acknowledgementResponse?.status === 'ok'}
        pollError={pollError}
        onClose={handleCloseDrawer}
        onSubmit={handleSubmit}
        onDownload={handleDownloadPdf}
      />
      <AcknowledgementSuccessModal
        open={openSuccessModal}
        flow={flow}
        content={acknowledgementContent}
        onGoToStatus={() => {
          window.location.href = `/packages/${packageId}/status`
        }}
        onGoToShareTab={() => {
          window.location.href = `/packages/${packageId}/board_members`
        }}
        onBack={goToRequirements}
      />
    </>
  )
}
